/**
 * Pool Scenarios Comparison Component
 *
 * Allows users to save and compare multiple pool configurations side-by-side
 */

import { Paper, Typography, Box, Grid, Card, CardContent, IconButton, Chip, Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import CompareIcon from '@mui/icons-material/Compare';
import type { PoolScenario, ScenarioResults } from '../../types';
import { calculateCapacity } from '../../core/capacity';
import { calculateReliability } from '../../core/reliability';
import { calculatePerformance } from '../../core/performance';
import { calculateEconomics } from '../../core/economics';

interface PoolScenariosProps {
  scenarios: PoolScenario[];
  onDeleteScenario: (id: string) => void;
  onCompare?: (scenario: PoolScenario) => void;
}

function calculateScenarioResults(scenario: PoolScenario): ScenarioResults {
  const capacity = calculateCapacity(scenario.topology, scenario.drive, scenario.zfs, scenario.utilization);
  const reliability = calculateReliability(scenario.topology, scenario.drive, scenario.utilization, capacity.finalUsableTiB);
  const performance = calculatePerformance(scenario.topology, scenario.drive, scenario.performance);
  const economics = calculateEconomics(scenario.topology, scenario.drive, scenario.economics, capacity);

  return {
    capacity,
    reliability,
    performance,
    economics,
  };
}

export function PoolScenarios({ scenarios, onDeleteScenario, onCompare }: PoolScenariosProps) {
  // Calculate results for all scenarios
  const resultsMap = new Map<string, ScenarioResults>();
  scenarios.forEach(scenario => {
    resultsMap.set(scenario.id, calculateScenarioResults(scenario));
  });

  // Find best values for highlighting
  const lowestRisk = Math.min(...scenarios.map(s => resultsMap.get(s.id)?.reliability.totalDataLossProbability ?? Infinity));
  const highestCapacity = Math.max(...scenarios.map(s => resultsMap.get(s.id)?.capacity.practicalUsableTiB ?? 0));
  const lowestCost = Math.min(...scenarios.map(s => resultsMap.get(s.id)?.economics.costPerUsableTB ?? Infinity));
  const highestIOPS = Math.max(...scenarios.map(s => resultsMap.get(s.id)?.performance.effectiveIOPS ?? 0));

  if (scenarios.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Pool Scenarios Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No scenarios saved yet. Use the "Save Current as Scenario" button to compare configurations.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Pool Scenarios Comparison
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Comparing {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''}
      </Typography>

      <Grid container spacing={2}>
        {scenarios.map((scenario) => {
          const results = resultsMap.get(scenario.id);
          if (!results) return null;

          const isBestRisk = results.reliability.totalDataLossProbability === lowestRisk;
          const isBestCapacity = results.capacity.practicalUsableTiB === highestCapacity;
          const isBestCost = results.economics.costPerUsableTB === lowestCost;
          const isBestIOPS = results.performance.effectiveIOPS === highestIOPS;

          return (
            <Grid item xs={12} md={6} lg={4} key={scenario.id}>
              <Card
                sx={{
                  height: '100%',
                  border: isBestRisk || isBestCapacity || isBestCost || isBestIOPS ? 2 : 1,
                  borderColor: isBestRisk || isBestCapacity || isBestCost || isBestIOPS ? 'success.main' : 'divider',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" noWrap>
                      {scenario.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {onCompare && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CompareIcon fontSize="small" />}
                          onClick={() => onCompare(scenario)}
                          sx={{ fontSize: '0.7rem' }}
                        >
                          Compare
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => onDeleteScenario(scenario.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Configuration
                    </Typography>
                    <Typography variant="body2">
                      {scenario.topology.raidLevel.toUpperCase()}, {scenario.topology.drivesPerVdev}D × {scenario.topology.vdevCount}V
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {results.economics.totalDrives} drives × {scenario.drive.driveSizeTB}TB
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Practical Usable Capacity
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {results.capacity.practicalUsableTiB.toFixed(1)} TiB
                      </Typography>
                      {isBestCapacity && (
                        <Chip label="Best" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {results.capacity.usablePercent.toFixed(0)}% efficiency • {results.capacity.finalUsableTiB.toFixed(1)} TiB before free space reserve
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Data Loss Risk
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        color={
                          results.reliability.totalDataLossProbability > 0.05
                            ? 'error.main'
                            : results.reliability.totalDataLossProbability > 0.01
                            ? 'warning.main'
                            : 'success.main'
                        }
                      >
                        {(results.reliability.totalDataLossProbability * 100).toFixed(3)}%
                      </Typography>
                      {isBestRisk && (
                        <Chip label="Best" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {results.reliability.resilverTimeHours.toFixed(1)}h resilver
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Performance
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {results.performance.effectiveIOPS.toFixed(0)} IOPS
                      </Typography>
                      {isBestIOPS && (
                        <Chip label="Best" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        Read: {results.performance.sequentialReadMBps.toFixed(0)} MB/s seq, {results.performance.randomReadIOPS.toFixed(0)} IOPS random
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Write: {results.performance.sequentialWriteMBps.toFixed(0)} MB/s seq, {results.performance.randomWriteIOPS.toFixed(0)} IOPS random
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Economics
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body1" fontWeight="medium">
                        ${results.economics.costPerUsableTB.toFixed(0)}/TiB
                      </Typography>
                      {isBestCost && (
                        <Chip label="Best" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      ${results.economics.hardwareCost.toFixed(0)} hardware
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Tip:</strong> "Best" badges highlight the scenario with the lowest risk, highest capacity,
          lowest cost per TiB, or highest IOPS. Use these comparisons to find the optimal configuration for your needs.
        </Typography>
      </Box>
    </Paper>
  );
}
